import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ComponentStore } from '@ngrx/component-store';
import { Store } from '@ngrx/store';
import { tap } from 'rxjs';
import { AggregierteAuswertungLayoutDto } from '../api/models';
import { MonatsauswertungLayoutDto } from '../api/models/monatsauswertung-layout-dto';
import * as fromMonatsauswertungenLayouts from '../store/auswertung-layouts/auswertung-layouts.selectors';
import * as fromRouter from '../store/router/router.selectors';

export type Mode = 'monatsauswertungen-list' | 'monatsauswertungen-create' | 'monatsauswertungen-edit' | 'aggregierteauswertungen-list' | 'aggregierteauswertungen-create' | 'aggregierteauswertungen-edit';

interface MonatsauswertungLayoutsPageComponentState {
  readonly monatsauswertungenLayouts: MonatsauswertungLayoutDto[] | undefined;
  readonly aggregierteauswertungenLayouts: AggregierteAuswertungLayoutDto[] | undefined;
  readonly loading: boolean;
  readonly selectedMonatsauswertungLayoutId: string | undefined;
  readonly selectedAggregierteAuswertungLayoutId: string | undefined;
  readonly mode: Mode;
}

@Injectable()
export class MonatsauswertungLayoutsPageComponentStore extends ComponentStore<MonatsauswertungLayoutsPageComponentState> {
  private readonly store = inject(Store);

  readonly setSelectMonatsauswertungLayoutId = this.updater(
    (
      state,
      selectedMonatsauswertungLayoutId: string | undefined,
    ): MonatsauswertungLayoutsPageComponentState => ({
      ...state,
      selectedMonatsauswertungLayoutId,
    }),
  );

  readonly setSelectAggregierteAuswertungLayoutId = this.updater(
    (
      state,
      selectedAggregierteAuswertungLayoutId: string | undefined,
    ): MonatsauswertungLayoutsPageComponentState => ({
      ...state,
      selectedAggregierteAuswertungLayoutId,
    }),
  );

  readonly loading$ = this.select((state) => state.loading);
  readonly mode$ = this.select((state) => state.mode);
  readonly monatsauswertungenLayouts = this.select(
    (state) => state.monatsauswertungenLayouts,
  );
  readonly selectedMonatsauswertungLayoutId$ = this.select(
    (state) => state.selectedMonatsauswertungLayoutId,
  );
  readonly selectSelectedMonatsauswertungLayout$ = this.select((state) =>
    state.monatsauswertungenLayouts?.find(
      (layout) => layout.id === state.selectedMonatsauswertungLayoutId,
    ),
  );
  readonly selectMode$ = this.select((state) => state.mode);

  readonly aggregierteAuswertungenLayouts = this.select(
    (state) => state.aggregierteauswertungenLayouts,
  );

  readonly selectedAggregierteAuswertungLayoutId$ = this.select(
    (state) => state.selectedAggregierteAuswertungLayoutId,
  );

  readonly selectSelectedAggregierteAuswertungLayout$ = this.select((state) =>
    state.aggregierteauswertungenLayouts?.find(
      (layout) => layout.id === state.selectedAggregierteAuswertungLayoutId,
    ),
  );

  constructor() {
    super({
      loading: false,
      mode: 'monatsauswertungen-list',
      monatsauswertungenLayouts: undefined,
      selectedMonatsauswertungLayoutId: undefined,
      aggregierteauswertungenLayouts: undefined,
      selectedAggregierteAuswertungLayoutId: undefined,
    });
    this.subscribeToLoading();
    this.subscribeToLayouts();
    this.subscribeToRouteData();
  }

  private subscribeToRouteData() {
    this.store
      .select(fromRouter.selectRouteData)
      .pipe(
        takeUntilDestroyed(),
        tap((data) => {
          const mode = data['mode'] as Mode | undefined;
          if (mode) {
            this.patchState({ mode });
          }
        }),
      )
      .subscribe();

    this.store
      .select(fromRouter.selectRouteParam('id'))
      .pipe(
        takeUntilDestroyed(),
        tap((id) => {
          if (this.get().mode.includes('aggregierteauswertungen')) {
            this.setSelectAggregierteAuswertungLayoutId(id);
          } else {
            this.setSelectMonatsauswertungLayoutId(id);
          }
        }),
      )
      .subscribe();
  }

  private subscribeToLayouts() {
    this.store
      .select(fromMonatsauswertungenLayouts.selectMonatsauswertungLayouts)
      .pipe(
        takeUntilDestroyed(),
        tap((monatsauswertungenLayouts) =>
          this.patchState({ monatsauswertungenLayouts }),
        ),
      )
      .subscribe();

    this.store
      .select(fromMonatsauswertungenLayouts.selectAggregierteAuswertungenLayouts)
      .pipe(
        takeUntilDestroyed(),
        tap((aggregierteauswertungenLayouts) =>
          this.patchState({ aggregierteauswertungenLayouts }),
        ),
      )
      .subscribe();
  }

  private subscribeToLoading() {
    this.store
      .select(
        fromMonatsauswertungenLayouts.selectMonatsauswertungLayoutsLoading,
      )
      .pipe(
        takeUntilDestroyed(),
        tap((loading) => this.patchState({ loading })),
      )
      .subscribe();

    this.store
      .select(
        fromMonatsauswertungenLayouts.selectAggregierteAuswertungenLayoutsLoading,
      )
      .pipe(
        takeUntilDestroyed(),
        tap((loading) => this.patchState({ loading })),
      )
      .subscribe();
  }
}
